import React from 'react';
import { SeatBookingPolicy, SeatInventoryItem, SeatPlanItem, SeatRequestItem, scheduledSeatService } from '../services/scheduledSeatService';
import { ScheduledCommandPanelProps } from '../components/ScheduledCommandPanel/types';
import { fmtDateTime, normalizePassengerRows, toCsvCell } from '../components/ScheduledCommandPanel/utils';
import { useScheduledOffersFlow } from './scheduledCommand/useScheduledOffersFlow';
import { useScheduledPlanAnalytics } from './scheduledCommand/useScheduledPlanAnalytics';
import { useScheduledTicketingFlow } from './scheduledCommand/useScheduledTicketingFlow';

export const useScheduledCommandPanelModel = ({
  onRefreshSignal = 0,
  onNotify,
  routes = [],
  aircrafts = [],
  customers = [],
  operatorDiscountCap = 0,
  currentUser = null,
}: ScheduledCommandPanelProps) => {
  const [inventory, setInventory] = React.useState<SeatInventoryItem[]>([]);
  const [seatPlan, setSeatPlan] = React.useState<SeatPlanItem[]>([]);
  const [requests, setRequests] = React.useState<SeatRequestItem[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [requestSearch, setRequestSearch] = React.useState('');
  const [requestLaneFilter, setRequestLaneFilter] = React.useState<'ALL' | 'OPEN' | 'OFFER' | 'PENDING' | 'CLOSED'>('ALL');
  const [inventorySearch, setInventorySearch] = React.useState('');
  const [seatPlanSearch, setSeatPlanSearch] = React.useState('');
  const [activeView, setActiveView] = React.useState<'OPERATIONS' | 'REQUESTS' | 'INVENTORY' | 'SEAT_PLAN'>('OPERATIONS');
  const [showComposer, setShowComposer] = React.useState(false);
  const [density, setDensity] = React.useState<'COMFORT' | 'DENSE'>('COMFORT');
  const [myQueueOnly, setMyQueueOnly] = React.useState(false);
  const [selectedPlan, setSelectedPlan] = React.useState<SeatPlanItem | null>(null);
  const [bookingPolicy, setBookingPolicy] = React.useState<SeatBookingPolicy>({ minHoursBeforeDeparture: 0 });
  const [seatPlanDate, setSeatPlanDate] = React.useState('');
  const [expandedPlanId, setExpandedPlanId] = React.useState('');
  const [focusRequestId, setFocusRequestId] = React.useState('');
  const [calendarMonth, setCalendarMonth] = React.useState(() => {
    const now = new Date();
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString().slice(0, 10);
  });

  const calendarWindow = React.useMemo(() => {
    const base = new Date(`${calendarMonth}T00:00:00.000Z`);
    if (Number.isNaN(base.getTime())) return { from: undefined as string | undefined, to: undefined as string | undefined };
    const start = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth(), 1));
    start.setUTCDate(start.getUTCDate() - 7);
    const end = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth() + 1, 0));
    end.setUTCDate(end.getUTCDate() + 7);
    return {
      from: start.toISOString(),
      to: end.toISOString(),
    };
  }, [calendarMonth]);

  const [newRequest, setNewRequest] = React.useState({
    customerName: '',
    fromEmail: '',
    fromCode: 'ARK',
    toCode: 'SEU',
    pax: 1,
    travelDate: new Date().toISOString().slice(0, 10),
    preferredTime: '10:00',
    notes: '',
  });

  const [newInventory, setNewInventory] = React.useState({
    source: 'ADHOC_RELEASE' as 'EMPTY_LEG' | 'FIXED_ROUTE' | 'ADHOC_RELEASE',
    routeId: '',
    aircraftType: '',
    departureDate: new Date().toISOString().slice(0, 10),
    useRoutePricing: true,
    fromCode: 'ARK',
    toCode: 'SEU',
    departureAt: `${new Date().toISOString().slice(0, 10)}T10:00:00.000Z`,
    totalSeats: 4,
    availableSeats: 4,
    seatPriceUSD: 250,
    title: '',
  });

  const activeRoutes = React.useMemo(
    () => (Array.isArray(routes) ? routes.filter((r) => r.isActive !== false) : []),
    [routes]
  );

  const selectedRoute = React.useMemo(
    () => activeRoutes.find((r) => r.id === newInventory.routeId) || null,
    [activeRoutes, newInventory.routeId]
  );

  const selectableAircrafts = React.useMemo(() => {
    if (!Array.isArray(aircrafts) || !aircrafts.length) return [];
    const routeAircraftIds = selectedRoute?.aircraftRates?.map((r) => r.aircraftId).filter(Boolean) || [];
    const base = routeAircraftIds.length
      ? aircrafts.filter((a) => routeAircraftIds.includes(a.id))
      : aircrafts;
    return base.filter((a) => a.status !== 'AOG');
  }, [aircrafts, selectedRoute]);

  const selectableAircraftTypes = React.useMemo(() => {
    const map = new Map<string, { name: string; maxPax: number; count: number }>();
    selectableAircrafts.forEach((a) => {
      const key = String(a.name || '').trim();
      if (!key) return;
      const existing = map.get(key);
      if (!existing) {
        map.set(key, { name: key, maxPax: Number(a.maxPax || 0), count: 1 });
      } else {
        map.set(key, {
          name: key,
          maxPax: Math.max(existing.maxPax, Number(a.maxPax || 0)),
          count: existing.count + 1,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [selectableAircrafts]);

  const routeStationCodes = React.useMemo(() => {
    const set = new Set<string>();
    activeRoutes.forEach((r) => {
      if (r.from) set.add(String(r.from).toUpperCase());
      if (r.to) set.add(String(r.to).toUpperCase());
    });
    return Array.from(set).sort((a: string, b: string) => a.localeCompare(b));
  }, [activeRoutes]);

  const stationOptions = React.useMemo(() => {
    const set = new Set<string>(routeStationCodes);
    if (newInventory.fromCode) set.add(String(newInventory.fromCode).toUpperCase());
    if (newInventory.toCode) set.add(String(newInventory.toCode).toUpperCase());
    return Array.from(set).sort((a: string, b: string) => a.localeCompare(b));
  }, [routeStationCodes, newInventory.fromCode, newInventory.toCode]);

  React.useEffect(() => {
    if (!activeRoutes.length) return;
    setNewInventory((prev) => {
      if (prev.routeId) return prev;
      const first = activeRoutes[0];
      const date = prev.departureDate || new Date().toISOString().slice(0, 10);
      const routeTime = first.departureTime || '08:00';
      return {
        ...prev,
        routeId: first.id,
        fromCode: first.from,
        toCode: first.to,
        departureAt: new Date(`${date}T${routeTime}:00.000Z`).toISOString(),
      };
    });
  }, [activeRoutes]);

  React.useEffect(() => {
    if (newInventory.source !== 'FIXED_ROUTE' || !selectedRoute) return;
    setNewInventory((prev) => {
      const date = prev.departureDate || new Date().toISOString().slice(0, 10);
      const routeTime = selectedRoute.departureTime || '08:00';
      const nextDeparture = new Date(`${date}T${routeTime}:00.000Z`).toISOString();
      const shouldPriceFromRoute = prev.useRoutePricing !== false;
      return {
        ...prev,
        fromCode: selectedRoute.from,
        toCode: selectedRoute.to,
        departureAt: nextDeparture,
        seatPriceUSD: shouldPriceFromRoute ? Number(selectedRoute.rackRateUSD || 0) : prev.seatPriceUSD,
      };
    });
  }, [selectedRoute, newInventory.source]);

  React.useEffect(() => {
    if (!newInventory.aircraftType || !selectableAircraftTypes.length) return;
    const acType = selectableAircraftTypes.find((a) => a.name === newInventory.aircraftType);
    if (!acType) return;
    const seats = Math.max(1, Number(acType.maxPax || 1));
    setNewInventory((prev) => ({
      ...prev,
      totalSeats: seats,
      availableSeats: Math.min(seats, Math.max(0, Number(prev.availableSeats || seats))),
    }));
  }, [newInventory.aircraftType, selectableAircraftTypes]);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [inventoryData, seatPlanData, requestData, bookingPolicyData] = await Promise.all([
        scheduledSeatService.listInventory({ limit: 20 }),
        scheduledSeatService.listSeatPlan({
          limit: 300,
          ...(calendarWindow.from ? { departureFrom: calendarWindow.from } : {}),
          ...(calendarWindow.to ? { departureTo: calendarWindow.to } : {}),
        }),
        scheduledSeatService.listRequests({
          limit: 20,
          ...(myQueueOnly && currentUser?.id ? { assignedToUserId: currentUser.id } : {}),
        }),
        scheduledSeatService.getBookingPolicy(),
      ]);
      setInventory(inventoryData);
      setSeatPlan(seatPlanData);
      setRequests(requestData);
      setBookingPolicy({
        minHoursBeforeDeparture: Math.max(0, Number(bookingPolicyData?.minHoursBeforeDeparture || 0)),
      });
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load scheduled command data');
    } finally {
      setLoading(false);
    }
  }, [myQueueOnly, currentUser?.id, calendarWindow.from, calendarWindow.to]);

  React.useEffect(() => {
    load();
  }, [load, onRefreshSignal]);

  React.useEffect(() => {
    try {
      const focusDate = localStorage.getItem('ma_scheduled_focus_date') || '';
      const focusInventory = localStorage.getItem('ma_scheduled_focus_inventory') || '';
      const focusRequest = localStorage.getItem('ma_scheduled_focus_request') || '';
      if (!focusDate) return;
      const dt = new Date(`${focusDate}T00:00:00.000Z`);
      if (!Number.isNaN(dt.getTime())) {
        const monthIso = new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), 1)).toISOString().slice(0, 10);
        setActiveView('OPERATIONS');
        setCalendarMonth(monthIso);
        setSeatPlanDate(focusDate);
        setExpandedPlanId(focusInventory);
        if (focusRequest) setFocusRequestId(focusRequest);
      }
      localStorage.removeItem('ma_scheduled_focus_date');
      localStorage.removeItem('ma_scheduled_focus_inventory');
      localStorage.removeItem('ma_scheduled_focus_request');
    } catch {
      // ignore storage access issues
    }
  }, []);

  React.useEffect(() => {
    const timer = window.setInterval(() => {
      load();
    }, 30_000);
    return () => window.clearInterval(timer);
  }, [load]);

  const fireNotify = (title: string, message: string, type: 'SUCCESS' | 'ERROR' | 'WARNING') => {
    if (onNotify) onNotify(title, message, type);
  };

  const exportManifestCsv = (plan: SeatPlanItem) => {
    const rows: string[] = [];
    rows.push([
      'Flight',
      'Departure',
      'Status',
      'Customer',
      'Customer Email',
      'Booking ID',
      'PNR',
      'Seats',
      'Passenger #',
      'Passenger Name',
      'Passenger ID',
      'Issued At',
      'Issued By',
    ].join(','));

    plan.bookings.forEach((b) => {
      const passengers = normalizePassengerRows((b as any)?.metadata || {});
      if (!passengers.length) {
        rows.push([
          `${plan.fromCode}-${plan.toCode}`,
          fmtDateTime(plan.departureAt),
          b.status,
          b.customerName || '',
          b.customerEmail || '',
          (b as any).bookingId || (b as any)?.metadata?.bookingId || '',
          (b as any).pnrCode || (b as any)?.metadata?.pnrCode || '',
          b.seats,
          '',
          '',
          '',
          fmtDateTime(b.createdAt),
          b.createdByName || '',
        ].map(toCsvCell).join(','));
        return;
      }
      passengers.forEach((p: any, idx: number) => {
        rows.push([
          `${plan.fromCode}-${plan.toCode}`,
          fmtDateTime(plan.departureAt),
          b.status,
          b.customerName || '',
          b.customerEmail || '',
          (b as any).bookingId || (b as any)?.metadata?.bookingId || '',
          (b as any).pnrCode || (b as any)?.metadata?.pnrCode || '',
          b.seats,
          p?.index || idx + 1,
          p?.name || '',
          p?.idNumber || '',
          fmtDateTime(b.createdAt),
          b.createdByName || '',
        ].map(toCsvCell).join(','));
      });
    });

    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `manifest-${plan.fromCode}-${plan.toCode}-${new Date(plan.departureAt).toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    fireNotify('Manifest Exported', 'CSV manifest downloaded successfully.', 'SUCCESS');
  };

  const createRequest = async () => {
    try {
      await scheduledSeatService.createRequest({
        category: 'SCHEDULED',
        customerName: newRequest.customerName || undefined,
        fromEmail: newRequest.fromEmail || undefined,
        fromCode: newRequest.fromCode || undefined,
        toCode: newRequest.toCode || undefined,
        pax: Number(newRequest.pax) || 1,
        travelDate: newRequest.travelDate ? `${newRequest.travelDate}T00:00:00.000Z` : undefined,
        preferredTime: newRequest.preferredTime || undefined,
        notes: newRequest.notes || undefined,
      });
      fireNotify('Seat Request Created', 'Scheduled request is now in command chain queue.', 'SUCCESS');
      load();
    } catch (e: any) {
      fireNotify('Create Failed', e?.response?.data?.error || e?.message || 'Failed to create request', 'ERROR');
    }
  };

  const createInventory = async () => {
    try {
      const fromCode = newInventory.source === 'FIXED_ROUTE' && selectedRoute ? selectedRoute.from : newInventory.fromCode;
      const toCode = newInventory.source === 'FIXED_ROUTE' && selectedRoute ? selectedRoute.to : newInventory.toCode;
      const departureAt = newInventory.source === 'FIXED_ROUTE' && selectedRoute
        ? new Date(`${newInventory.departureDate || new Date().toISOString().slice(0, 10)}T${selectedRoute.departureTime || '08:00'}:00.000Z`).toISOString()
        : newInventory.departureAt;
      const seatPriceUSD = newInventory.source === 'FIXED_ROUTE' && newInventory.useRoutePricing
        ? undefined
        : Number(newInventory.seatPriceUSD) || 0;

      await scheduledSeatService.createInventory({
        source: newInventory.source,
        fromCode,
        toCode,
        departureAt,
        totalSeats: Number(newInventory.totalSeats) || 1,
        availableSeats: Number(newInventory.availableSeats) || Number(newInventory.totalSeats) || 1,
        seatPriceUSD,
        title: newInventory.title || undefined,
        metadata: {
          aircraftType: newInventory.aircraftType || null,
          routeId: selectedRoute?.id || null,
          routeSource: newInventory.source === 'FIXED_ROUTE' ? 'SYSTEM_REGISTRY' : 'MANUAL',
        },
      });
      fireNotify('Seat Inventory Added', 'Inventory slot added to scheduled seat pool.', 'SUCCESS');
      setActiveView('OPERATIONS');
      if (newInventory.departureDate) setSeatPlanDate(newInventory.departureDate);
      load();
    } catch (e: any) {
      fireNotify('Create Failed', e?.response?.data?.error || e?.message || 'Failed to create inventory', 'ERROR');
    }
  };

  const setOutcome = async (
    id: string,
    outcome: 'OFFER_SENT' | 'ALTERNATIVE_SENT' | 'PENDING_APPROVAL' | 'DECLINED',
    note?: string
  ) => {
    try {
      await scheduledSeatService.setRequestOutcome(id, outcome, note);
      fireNotify('Request Updated', `Request moved to ${outcome}.`, 'SUCCESS');
      load();
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'Failed to update request';
      fireNotify('Update Failed', msg, 'ERROR');
    }
  };

  const analytics = useScheduledPlanAnalytics({
    requests,
    seatPlan,
    inventory,
    requestSearch,
    requestLaneFilter,
    inventorySearch,
    seatPlanSearch,
    seatPlanDate,
    calendarMonth,
    bookingPolicyMinHoursBeforeDeparture: bookingPolicy.minHoursBeforeDeparture,
    setCalendarMonth,
    setSeatPlanDate,
    setExpandedPlanId,
  });

  const {
    calendarDays,
    dedupedSeatPlan,
    filteredInventory,
    filteredRequests,
    filteredSeatPlan,
    laneByStatus,
    laneCounts,
    monthSummary,
    monthTitle,
    operationsDayPlans,
    selectCalendarDate,
    selectedDaySummary,
    shiftCalendarMonth,
  } = analytics;

  const offersFlow = useScheduledOffersFlow({
    customers,
    routes,
    operatorDiscountCap,
    dedupedSeatPlan,
    operationsDayPlans,
    load,
    fireNotify,
    setActiveView,
    setCalendarMonth,
    setSeatPlanDate,
    setExpandedPlanId,
  });

  const {
    focusInventoryInCalendar,
    isSendingReviewOffer,
    offerFromRequest,
    openOfferReview,
    reviewClientName,
    reviewClientPhone,
    reviewCustomerDiscountPercent,
    reviewMessage,
    reviewOperatorDiscountPercent,
    reviewPricing,
    reviewRequest,
    reviewSeatPriceUSD,
    reviewSeats,
    sendOfferFromReview,
    setReviewClientName,
    setReviewClientPhone,
    setReviewCustomerDiscountPercent,
    setReviewMessage,
    setReviewOperatorDiscountPercent,
    setReviewRequest,
    setReviewSeatPriceUSD,
    setReviewSeats,
  } = offersFlow;

  const setFlightOpsStatus = async (id: string, opsStatus: 'OPEN' | 'CLOSED' | 'DEPARTED' | 'CANCELLED') => {
    try {
      await scheduledSeatService.setInventoryOpsStatus(id, { opsStatus });
      fireNotify('Flight Updated', `Flight set to ${opsStatus}`, 'SUCCESS');
      await load();
    } catch (e: any) {
      fireNotify('Update Failed', e?.response?.data?.error || e?.message || 'Failed to update flight status', 'ERROR');
    }
  };

  const ticketingFlow = useScheduledTicketingFlow({
    routes,
    customers,
    operatorDiscountCap,
    currentUserRole: currentUser?.role,
    bookingPolicyMinHoursBeforeDeparture: bookingPolicy.minHoursBeforeDeparture,
    load,
    fireNotify,
  });

  return {
    activeRoutes,
    activeView,
    bookingPolicy,
    calendarDays,
    calendarMonth,
    calendarWindow,
    createInventory,
    createRequest,
    customers,
    dedupedSeatPlan,
    density,
    error,
    expandedPlanId,
    exportManifestCsv,
    filteredInventory,
    filteredRequests,
    filteredSeatPlan,
    fireNotify,
    focusInventoryInCalendar,
    focusRequestId,
    inventory,
    inventorySearch,
    laneByStatus,
    laneCounts,
    load,
    loading,
    monthSummary,
    monthTitle,
    myQueueOnly,
    newInventory,
    newRequest,
    offerFromRequest,
    openOfferReview,
    operationsDayPlans,
    operatorDiscountCap,
    requestLaneFilter,
    requestSearch,
    requests,
    reviewClientName,
    reviewClientPhone,
    reviewCustomerDiscountPercent,
    reviewMessage,
    reviewOperatorDiscountPercent,
    reviewPricing,
    reviewRequest,
    reviewSeatPriceUSD,
    reviewSeats,
    routeStationCodes,
    seatPlan,
    seatPlanDate,
    seatPlanSearch,
    selectCalendarDate,
    selectableAircraftTypes,
    selectableAircrafts,
    selectedDaySummary,
    selectedPlan,
    selectedRoute,
    sendOfferFromReview,
    setActiveView,
    setBookingPolicy,
    setCalendarMonth,
    setDensity,
    setError,
    setExpandedPlanId,
    setFlightOpsStatus,
    setFocusRequestId,
    setInventory,
    setInventorySearch,
    setMyQueueOnly,
    setNewInventory,
    setNewRequest,
    setOutcome,
    setRequestLaneFilter,
    setRequestSearch,
    setRequests,
    setReviewClientName,
    setReviewClientPhone,
    setReviewCustomerDiscountPercent,
    setReviewMessage,
    setReviewOperatorDiscountPercent,
    setReviewRequest,
    setReviewSeatPriceUSD,
    setReviewSeats,
    setSeatPlan,
    setSeatPlanDate,
    setSeatPlanSearch,
    setSelectedPlan,
    setShowComposer,
    shiftCalendarMonth,
    showComposer,
    stationOptions,
    ...ticketingFlow,
    isSendingReviewOffer,
  };
};

export type ScheduledCommandPanelModel = ReturnType<typeof useScheduledCommandPanelModel>;
